// src/pages/prayers/prayerList/prayerList.js
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Button, DatePicker, Modal, Pagination, Table, Tooltip } from "antd";
import { FaVolumeUp, FaCheck, FaEye, FaTrash, FaTimes } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./prayerList.css";

const { RangePicker } = DatePicker;

// ─── Static config (no need to memoize inside component) ───────────────────────
const FILTERS_CONFIG = {
  submissionDate: {
    label: "Submission Date",
    options: [
      { value: "last7Days", label: "Last 7 Days" },
      { value: "last30Days", label: "Last 30 Days" },
      { value: "dateRange", label: "Custom Date Range" }
    ]
  },
  visibilityStatus: {
    label: "Visibility",
    options: [
      { value: "public", label: "Public" },
      { value: "private", label: "Private" }
    ]
  },
  approvalStatus: {
    label: "Status",
    options: [
      { value: "approved", label: "Approved" },
      { value: "submitted", label: "Submitted" }
    ]
  }
};

// ─── Initial state ─────────────────────────────────────────────────────────────
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  prayers: [],
  currentPage: 1,
  pageSize: 10,
  sortConfig: { key: "created_at", direction: "desc" },
  showApproveModal: false,
  selectedPrayerId: null,
  isApproving: false,
  showDeleteModal: false,
  deletePrayerId: null,
  isDeleting: false,
  showDateRangeModal: false,
  tempFromDate: null,
  tempToDate: null,
  fromDate: null,
  toDate: null
};

// ─── Reducer ───────────────────────────────────────────────────────────────────
const prayerReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setPrayers":
      return { ...state, prayers: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload };
    case "setApproveModal":
      return {
        ...state,
        showApproveModal: action.payload.show,
        selectedPrayerId: action.payload.prayerId || null
      };
    case "setIsApproving":
      return { ...state, isApproving: action.payload };
    case "setDeleteModal":
      return {
        ...state,
        showDeleteModal: action.payload.show,
        deletePrayerId: action.payload.prayerId || null
      };
    case "setIsDeleting":
      return { ...state, isDeleting: action.payload };
    case "setDateRangeModal":
      return { ...state, showDateRangeModal: action.payload };
    case "setTempDateRange":
      return {
        ...state,
        tempFromDate: action.payload.from,
        tempToDate: action.payload.to
      };
    case "setDateRange":
      return {
        ...state,
        fromDate: action.payload.from,
        toDate: action.payload.to
      };
    case "updatePrayerStatus":
      return {
        ...state,
        prayers: state.prayers.map((prayer) => (prayer.requestId === action.payload.id ? { ...prayer, status: action.payload.status } : prayer))
      };
    case "deletePrayer":
      return {
        ...state,
        prayers: state.prayers.filter((prayer) => prayer.requestId !== action.payload.id)
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1,
        fromDate: null,
        toDate: null,
        tempFromDate: null,
        tempToDate: null,
        showDateRangeModal: false
      };
    default:
      return state;
  }
};

// ─── Component ─────────────────────────────────────────────────────────────────
function PrayerManagement() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(prayerReducer, initialState);

  const {
    isLoading,
    searchTerm,
    filters,
    prayers,
    currentPage,
    pageSize,
    sortConfig,
    showApproveModal,
    selectedPrayerId,
    isApproving,
    showDeleteModal,
    deletePrayerId,
    isDeleting,
    showDateRangeModal,
    tempFromDate,
    tempToDate,
    fromDate,
    toDate
  } = state;

  // ─── Data fetching ───────────────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const fetchPrayerRequests = async () => {
      dispatch({ type: "setLoading", payload: true });
      try {
        const token = await getAuthToken();
        const response = await axios.get(API_ENDPOINTS.PRAYER_REQUESTS_ADMIN, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isMounted) {
          dispatch({ type: "setPrayers", payload: response.data || [] });
        }
      } catch (error) {
        console.error("Failed to fetch prayers:", error);
        if (isMounted) {
          showNotification("Failed to load prayer requests", "error", "Loading Error");
        }
      } finally {
        if (isMounted) {
          dispatch({ type: "setLoading", payload: false });
        }
      }
    };

    fetchPrayerRequests();
    return () => {
      isMounted = false;
    };
  }, [showNotification]);

  // ─── Filtering utilities ─────────────────────────────────────────────────────
  const filterByDate = useCallback(
    (items, dateFilter) => {
      if (!dateFilter) return items;

      switch (dateFilter) {
        case "last7Days": {
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - 7);
          return items.filter((item) => new Date(item.created_at) >= cutoff);
        }
        case "last30Days": {
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - 30);
          return items.filter((item) => new Date(item.created_at) >= cutoff);
        }
        case "dateRange": {
          if (!fromDate || !toDate) return items;
          const from = new Date(fromDate);
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          return items.filter((item) => {
            const date = new Date(item.created_at);
            return date >= from && date <= to;
          });
        }
        default:
          return items;
      }
    },
    [fromDate, toDate]
  );

  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;
    const searchLower = term.toLowerCase().trim();
    return items.filter((item) => {
      const fields = [
        item.name || "",
        item.title || "",
        item.description || "",
        item.created_at
          ? new Date(item.created_at).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric"
            })
          : ""
      ];
      return fields.some((f) => f.toLowerCase().includes(searchLower));
    });
  }, []);

  // ─── Derived / memoized data ─────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let result = [...prayers];

    result = filterByDate(result, filters.submissionDate);

    if (filters.visibilityStatus === "public") {
      result = result.filter((p) => p.is_public === "true" || p.is_public === true);
    } else if (filters.visibilityStatus === "private") {
      result = result.filter((p) => p.is_public === "false" || p.is_public === false);
    }

    if (filters.approvalStatus === "approved") {
      result = result.filter((p) => p.status === "Approved");
    } else if (filters.approvalStatus === "submitted") {
      result = result.filter((p) => p.status === "Submitted" || !p.status);
    }

    result = globalSearch(result, searchTerm);

    // FIX #1: Always apply sorting, regardless of whether a date filter is active
    if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        const valA = new Date(a[key] || a.created_at);
        const valB = new Date(b[key] || b.created_at);
        return direction === "asc" ? valA - valB : valB - valA;
      });
    }

    return result;
  }, [prayers, filters, searchTerm, filterByDate, globalSearch, sortConfig]);

  const currentPageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // FIX #2: Memoize excel data properly with useMemo instead of calling inline
  const excelData = useMemo(
    () =>
      filteredData.map((prayer) => ({
        "Request ID": prayer.requestId,
        Title: prayer.title,
        Description: prayer.description,
        "Submitted By": prayer.name,
        "Submitted On": prayer.created_at,
        Visibility: prayer.is_public === "true" ? "Public" : "Private",
        Status: prayer.status || "Submitted"
      })),
    [filteredData]
  );

  // ─── Event handlers ──────────────────────────────────────────────────────────
  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback(
    (newFilters) => {
      if (newFilters.submissionDate === "dateRange" && filters.submissionDate !== "dateRange") {
        dispatch({ type: "setDateRangeModal", payload: true });
        return;
      }

      if (filters.submissionDate === "dateRange" && newFilters.submissionDate !== "dateRange") {
        dispatch({ type: "setDateRange", payload: { from: null, to: null } });
        dispatch({
          type: "setTempDateRange",
          payload: { from: null, to: null }
        });
      }

      dispatch({ type: "setFilters", payload: newFilters });
    },
    [filters]
  );

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  const handleDateRangeChange = useCallback((_, dateStrings) => {
    dispatch({
      type: "setTempDateRange",
      payload: { from: dateStrings[0] || null, to: dateStrings[1] || null }
    });
  }, []);

  const applyDateRangeFilter = useCallback(() => {
    if (tempFromDate && tempToDate) {
      dispatch({
        type: "setDateRange",
        payload: { from: tempFromDate, to: tempToDate }
      });
      dispatch({
        type: "setFilters",
        payload: { ...filters, submissionDate: "dateRange" }
      });
      dispatch({ type: "setDateRangeModal", payload: false });
      showNotification("Date range filter applied successfully", "success");
    } else {
      showNotification("Please select both from and to dates", "warning");
    }
  }, [tempFromDate, tempToDate, filters, showNotification]);

  const handleDateRangeModalCancel = useCallback(() => {
    dispatch({ type: "setDateRangeModal", payload: false });
    dispatch({ type: "setTempDateRange", payload: { from: null, to: null } });
    const updatedFilters = { ...filters };
    delete updatedFilters.submissionDate;
    dispatch({ type: "setFilters", payload: updatedFilters });
  }, [filters]);

  // ─── Modal handlers ──────────────────────────────────────────────────────────
  const openApproveModal = useCallback((prayerId, e) => {
    e.stopPropagation();
    dispatch({ type: "setApproveModal", payload: { show: true, prayerId } });
  }, []);

  const closeApproveModal = useCallback(() => {
    dispatch({ type: "setApproveModal", payload: { show: false } });
  }, []);

  const openDeleteModal = useCallback((prayerId, e) => {
    e.stopPropagation();
    dispatch({ type: "setDeleteModal", payload: { show: true, prayerId } });
  }, []);

  const closeDeleteModal = useCallback(() => {
    dispatch({ type: "setDeleteModal", payload: { show: false } });
  }, []);

  const confirmApprove = useCallback(async () => {
    if (!selectedPrayerId) return;

    const currentPrayer = prayers.find((p) => p.requestId === selectedPrayerId);
    const isPublic = currentPrayer?.is_public === true || currentPrayer?.is_public === "true";

    if (!isPublic) {
      showNotification("This prayer cannot be approved because it's not public.", "warning", "Cannot Approve");
      dispatch({ type: "setIsApproving", payload: false });
      closeApproveModal();
      return;
    }

    const newStatus = currentPrayer?.status === "Approved" ? "Submitted" : "Approved";
    dispatch({ type: "setIsApproving", payload: true });

    try {
      const token = await getAuthToken();
      const response = await axios.put(
        API_ENDPOINTS.PRAYER_REQUESTS_ADMIN,
        { id: selectedPrayerId, status: newStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        dispatch({
          type: "updatePrayerStatus",
          payload: { id: selectedPrayerId, status: newStatus }
        });
        showNotification(`Prayer marked as ${newStatus}!`, newStatus === "Approved" ? "success" : "info", "Status Updated");
        closeApproveModal();
      } else {
        showNotification(response.data?.message || "Failed to update prayer.", "error", "Update Failed");
      }
    } catch (error) {
      console.error("Approval error:", error.response?.data || error.message);
      showNotification(error.response?.data?.message || "Failed to update prayer status.", "error", "Error");
    } finally {
      dispatch({ type: "setIsApproving", payload: false });
    }
  }, [selectedPrayerId, prayers, showNotification, closeApproveModal]);

  const confirmDelete = useCallback(async () => {
    if (!deletePrayerId) return;
    dispatch({ type: "setIsDeleting", payload: true });

    try {
      const token = await getAuthToken();
      await axios.delete(API_ENDPOINTS.PRAYER_REQUESTS_ADMIN, {
        data: { requestId: deletePrayerId },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      showNotification("Prayer deleted successfully!", "success", "Deleted");
      dispatch({ type: "deletePrayer", payload: { id: deletePrayerId } });
      closeDeleteModal();
    } catch (error) {
      console.error("Delete error:", error.response?.data || error.message);
      showNotification(error.response?.data?.message || "Failed to delete prayer.", "error", "Delete Failed");
    } finally {
      dispatch({ type: "setIsDeleting", payload: false });
    }
  }, [deletePrayerId, showNotification, closeDeleteModal]);

  // ─── Table columns ───────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "TITLE",
        dataIndex: "title",
        sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
        render: (title) => title || "-"
      },
      {
        title: "DESCRIPTION",
        dataIndex: "description",
        render: (description) => {
          if (!description) return "N/A";
          const words = description.split(" ");
          return words.length > 3 ? words.slice(0, 3).join(" ") + "..." : description;
        }
      },
      {
        title: "AUDIO",
        render: (_, record) => (record.audio_url ? <FaVolumeUp style={{ color: "hsl(175, 35%, 45%)", fontSize: "18px" }} /> : null),
        width: 80
      },
      {
        title: "SUBMITTED ON",
        dataIndex: "created_at",
        sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        render: (date) =>
          date
            ? new Date(date).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
            : "-"
      },
      {
        title: "SUBMITTED BY",
        dataIndex: "name",
        sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
        render: (name) => name || "-"
      },
      {
        title: "VISIBILITY",
        dataIndex: "is_public",
        sorter: (a, b) => {
          const valA = a.is_public === "true" ? "Public" : "Private";
          const valB = b.is_public === "true" ? "Public" : "Private";
          return valA.localeCompare(valB);
        },
        render: (is_public) => (
          <span
            style={{
              color: is_public === "true" ? "hsl(35, 35%, 10%)" : "hsl(0, 50%, 50%)"
            }}
          >
            {is_public === "true" ? "Public" : "Private"}
          </span>
        )
      },
      {
        title: "ACTIONS",
        render: (_, record) => {
          const isPublic = record.is_public === true || record.is_public === "true";
          const isApproved = record.status === "Approved";
          return (
            <div className="prl-action-buttons" onClick={(e) => e.stopPropagation()}>
              <Tooltip title={!isPublic ? "Private prayers cannot be approved" : isApproved ? "Unapprove" : "Approve"}>
                <button
                  className={`prl-table-approve-btn ${isApproved ? "approved" : ""} ${!isPublic ? "disabled" : ""}`}
                  disabled={!isPublic}
                  onClick={(e) => {
                    if (!isPublic) return;
                    openApproveModal(record.requestId, e);
                  }}
                >
                  {isApproved ? <FaTimes /> : <FaCheck />}
                </button>
              </Tooltip>

              <Tooltip title="View">
                <button className="prl-table-view-btn" onClick={() => navigate(`/prayerDetails/${record.requestId}`)}>
                  <FaEye />
                </button>
              </Tooltip>

              <Tooltip title="Delete">
                <button className="prl-table-delete-btn" onClick={(e) => openDeleteModal(record.requestId, e)}>
                  <FaTrash />
                </button>
              </Tooltip>
            </div>
          );
        },
        width: 200
      }
    ],
    [currentPage, pageSize, openApproveModal, openDeleteModal, navigate]
  );

  // ─── Derived modal title/body ─────────────────────────────────────────────────
  const selectedPrayer = prayers.find((p) => p.requestId === selectedPrayerId);
  const isSelectedApproved = selectedPrayer?.status === "Approved";

  return (
    <Layout
      navTitle="Prayer Requests"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={FILTERS_CONFIG}
            searchPlaceholder="Search across all fields..."
            searchTerm={searchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={excelData}
            excelFilename="prayer-list"
            excelSheetName="Prayers"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.requestId}
          rowClassName={(_, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
        />

        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      {/* Date Range Modal */}
      <Modal
        title="Select Date Range"
        open={showDateRangeModal}
        onCancel={handleDateRangeModalCancel}
        footer={[
          <Button key="cancel" onClick={handleDateRangeModalCancel}>
            Cancel
          </Button>,
          <Button key="apply" onClick={applyDateRangeFilter}>
            Apply Filter
          </Button>
        ]}
        width={450}
        centered={false}
        style={{ top: 120 }}
      >
        <div style={{ marginBottom: "20px" }}>
          <RangePicker
            style={{ width: "100%" }}
            value={tempFromDate && tempToDate ? [dayjs(tempFromDate), dayjs(tempToDate)] : []}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={["From Date", "To Date"]}
          />
        </div>
      </Modal>

      {/* Approve / Unapprove Modal */}
      <Modal
        title={isSelectedApproved ? "Remove Approval" : "Confirm Approval"}
        open={showApproveModal}
        onCancel={closeApproveModal}
        footer={[
          <Button className="prl-modal-cancel-btn" key="cancel" onClick={closeApproveModal}>
            Cancel
          </Button>,
          <Button className="prl-modal-confirm-btn" key="confirm" loading={isApproving} onClick={confirmApprove}>
            Confirm
          </Button>
        ]}
        centered
      >
        <p>{isSelectedApproved ? "Do you want to unapprove this prayer request?" : "Are you sure you want to approve this prayer request?"}</p>
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Confirm Delete"
        open={showDeleteModal}
        onCancel={closeDeleteModal}
        footer={[
          <Button className="prl-modal-cancel-btn" key="cancel" onClick={closeDeleteModal}>
            Cancel
          </Button>,
          <Button className="prl-modal-delete-btn" key="delete" loading={isDeleting} onClick={confirmDelete}>
            Delete
          </Button>
        ]}
        centered
      >
        <p>Do you want to delete this prayer request?</p>
      </Modal>
    </Layout>
  );
}

export default PrayerManagement;
